import { FC, useMemo } from 'react';
import { Link } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { format8 } from 'utils/number';
import type { DivisionOrderResult, DivisionOrderLineItemRef } from './types';
import { OperatorCellContent } from './operator-cell';
import styles from './expanded-line-items.module.scss';

interface Props {
  row: DivisionOrderResult;
}

// `owner_number` and `product` aren't on the DOLI mirror — they're pulled into
// the ES doc from per-line LLM citations (see
// Salesforce::DivisionOrder#line_llm_fields_by_doli_id).
interface LineRow extends Record<string, unknown> {
  rowKey: string;
  parent: DivisionOrderResult;
  item: DivisionOrderLineItemRef;
}

const columns: DataTableColumnDef<LineRow>[] = [
  {
    id: 'propertyName',
    accessorFn: (r) => r.item.propertyName,
    header: 'Property Name',
    size: 160,
    cell: ({ row }) => row.original.item.propertyName ?? '--',
  },
  {
    id: 'uwi',
    accessorFn: (r) => r.item.uwi,
    header: 'Well',
    size: 140,
    cell: ({ row }) => {
      const { uwi, importedWellApi, importedWellName } = row.original.item;
      if (uwi) return <Link to={`/wells/${uwi}`}>{uwi}</Link>;
      // ImportedWell match: no UWI and no /wells page — render the API number
      // (else the name) as plain text.
      return importedWellApi ?? importedWellName ?? '--';
    },
  },
  {
    id: 'propertyNumber',
    accessorFn: (r) => r.item.propertyNumber,
    header: 'Property No.',
    size: 120,
    cell: ({ row }) => row.original.item.propertyNumber ?? '--',
  },
  {
    id: 'product',
    accessorFn: (r) => r.item.product,
    header: 'Product',
    size: 110,
    cell: ({ row }) => row.original.item.product ?? '--',
  },
  {
    id: 'operator',
    accessorFn: (r) => r.parent.operator?.name ?? null,
    header: 'Operator',
    size: 200,
    cell: ({ row }) => (
      <OperatorCellContent operator={row.original.parent.operator} />
    ),
  },
  {
    id: 'ownerNumber',
    accessorFn: (r) => r.item.ownerNumber,
    header: 'Owner number',
    size: 130,
    cell: ({ row }) => row.original.item.ownerNumber ?? '--',
  },
  {
    id: 'owner',
    accessorFn: (r) => r.item.entityName ?? r.item.crmAccountName ?? null,
    header: 'Owner',
    size: 200,
    cell: ({ row }) => {
      const { entityId, entityName, crmAccountName } = row.original.item;
      if (entityId && entityName) {
        return <Link to={`/entities/${entityId}`}>{entityName}</Link>;
      }
      return crmAccountName ?? '--';
    },
  },
  {
    id: 'interestType',
    accessorFn: (r) => r.item.interestType,
    header: 'Interest type',
    size: 120,
    cell: ({ row }) => row.original.item.interestType ?? '--',
  },
  {
    id: 'interest',
    accessorFn: (r) => r.item.interest,
    header: 'Interest percentage',
    size: 140,
    meta: { type: 'percent', align: 'right' },
    cell: ({ row }) => format8(row.original.item.interest),
  },
];

const ExpandedLineItems: FC<Props> = ({ row }) => {
  const rows = useMemo<LineRow[]>(
    () =>
      (row.lineItems ?? []).map((item, idx) => ({
        rowKey: `${row.id}-${item.id || idx}`,
        parent: row,
        item,
      })),
    [row]
  );

  if (rows.length === 0) {
    return (
      <div className={styles.empty}>
        No properties / line items on this division order.
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <DataTable<LineRow>
        data={rows}
        columns={columns}
        getRowId={(r) => r.rowKey}
        config={{
          // Sub-table inside an expanded row: drop every chrome feature so we
          // get just headers + cells (no toolbar, no footer, no controls).
          globalSearch: false,
          columnFilters: false,
          columnReordering: false,
          columnResizing: false,
          columnPinning: false,
          columnVisibility: false,
          sorting: false,
          export: false,
          hideFooter: true,
        }}
      />
    </div>
  );
};

export { ExpandedLineItems };
